/**
 * FO-340 (batch-40) — medewerkerstatus in twee lagen: de gedeelde sets en
 * predicaten.
 *
 * Dit is het HART van FO-340. Zonder deze module is de rest een fix die over een
 * half jaar opnieuw uit elkaar loopt.
 *
 * ── Waarom dit bestaat ──────────────────────────────────────────────────────
 *
 * `Employee.status` probeerde twee onafhankelijke dingen tegelijk te zijn:
 *
 *   1. Waar sta je in je dienstverband? (CONCEPT -> ONBOARDING -> in dienst ->
 *      uit dienst). Dit hoort te bepalen WELKE ACTIES MOGEN.
 *   2. Ben je op dit moment inzetbaar? (actief / ziek / langdurig ziek /
 *      non-actief). Dit hoort te bepalen WAAR JE VERSCHIJNT (planbord,
 *      capaciteit, suggesties).
 *
 * Omdat het een veld was, verdrong `ZIEK` de waarde `ACTIEF` en sloot elke
 * `status: 'ACTIEF'`-where zieken ongemerkt uit van acties waar ziekte niets mee
 * te maken heeft (een verzuimgesprek plannen, bijvoorbeeld). Dat is ~60 keer met
 * de hand geschreven, in minstens zeven varianten. Niemand kon nog overzien
 * welke variant waar juist was.
 *
 * ── DE GOUDEN REGEL (spec §3.4) ─────────────────────────────────────────────
 *
 *   `werkstatus` blokkeert nooit een actie. `dienstverband` bepaalt nooit
 *   zichtbaarheid.
 *
 * Wie een `werkstatus` in een actie-guard schrijft, of een `dienstverband`
 * gebruikt om iemand uit een lijst te filteren, heeft de lagen weer door elkaar
 * gehaald. De enige uitzondering is klasse D (nudge-notificaties): een zieke
 * aanmanen om zijn uren te boeken is niet fout maar wel ongepast.
 *
 * Zelfde plek en vorm als `verlof-weergave.ts` (VERLOF_ACTIEF_STATUSSEN), dus
 * geen nieuw patroon. De Prisma-where-builders staan in
 * `apps/api/src/lib/medewerker-selectie.ts` — alleen de API queryt Prisma
 * direct.
 */

import { vandaagAmsterdam } from './dagtaken-datum'

/**
 * `startDate`, `endDate`, `nonActiefSinds` en `nonActiefTot` zijn DAGEN, geen
 * momenten (§12.12): UTC in de DB, Europe/Amsterdam bij interpretatie. Een
 * peildatum is meestal wél een moment (`new Date()`).
 *
 * Vergelijk je die twee rechtstreeks, dan verlies je de laatste dag: `endDate`
 * staat op 30-06 00:00 UTC en "nu" is 30-06 10:00, dus `endDate < nu` en de
 * medewerker valt op zijn eigen laatste werkdag al buiten het venster. Precies
 * het gat waar §12.12 voor waarschuwt ("een non-actief-stelling die om 23:30 NL
 * ingaat mag niet op de vorige dag landen").
 *
 * Daarom rekent alles hieronder in NL-KALENDERDAGEN. Voor een dag-instant is
 * `vandaagAmsterdam` een no-op; voor een moment normaliseert hij naar de
 * NL-dag. Beide kanten van elke vergelijking gaan er dus doorheen.
 */
export function alsNlDag(d: Date): Date {
  return vandaagAmsterdam(d)
}

/** FO-340 laag 1 — spiegelt de Prisma-enum `Dienstverband`. */
export type DienstverbandWaarde = 'CONCEPT' | 'ONBOARDING' | 'IN_DIENST' | 'UIT_DIENST'

/** FO-340 laag 2 — spiegelt de Prisma-enum `Werkstatus`. */
export type WerkstatusWaarde = 'ACTIEF' | 'ZIEK' | 'LANGDURIG_ZIEK' | 'NON_ACTIEF'

// ─── Laag 1: dienstverband-sets (bepalen WAT MAG) ───────────────────────────

/** Alles wat bestaat. Klasse A (dossier): nooit een blokkade. */
export const DIENSTVERBAND_BESTAAT = [
  'CONCEPT',
  'ONBOARDING',
  'IN_DIENST',
  'UIT_DIENST',
] as const satisfies readonly DienstverbandWaarde[]

/**
 * Klasse B (voorbereidend): opleiding inschrijven, uitrusting/abonnement/
 * materieel toewijzen, RI&E, NEN3140-aanwijzing. Mag zodra er een getekende
 * arbeidsrelatie is, dus vanaf ONBOARDING.
 */
export const DIENSTVERBAND_VOORBEREIDEND = [
  'ONBOARDING',
  'IN_DIENST',
] as const satisfies readonly DienstverbandWaarde[]

/** De arbeidsrelatie loopt nu. */
export const DIENSTVERBAND_IN_DIENST = ['IN_DIENST'] as const satisfies readonly DienstverbandWaarde[]

// ─── Laag 2: werkstatus-sets (bepalen WAAR JE VERSCHIJNT) ───────────────────

/** Inzetbaar nu: suggesties, matching, auto-toewijzing. */
export const WERKSTATUS_INZETBAAR = ['ACTIEF'] as const satisfies readonly WerkstatusWaarde[]

/** Afwezig: markeren op het bord, niet meetellen in de capaciteit. */
export const WERKSTATUS_AFWEZIG = [
  'ZIEK',
  'LANGDURIG_ZIEK',
  'NON_ACTIEF',
] as const satisfies readonly WerkstatusWaarde[]

/**
 * Standaard zichtbaar op het planbord-raster. Langdurig zieken en non-actieven
 * zitten er bewust NIET in: die staan ingeklapt achter een toggle (twaalf
 * langdurig zieken bovenaan het raster kosten elke dag scroll-tijd zonder ooit
 * relevant te zijn). Een gewone zieke staat er WEL in, met markering — een
 * planner die een monteur ziet verdwijnen weet niet of hij weg is of dat een
 * filter hem verbergt; een grijze rij met "ziek t/m vr" leest hij meteen.
 */
export const WERKSTATUS_PLANBORD_ZICHTBAAR = [
  'ACTIEF',
  'ZIEK',
] as const satisfies readonly WerkstatusWaarde[]

// ─── Actieklassen (spec §4.1) ───────────────────────────────────────────────

/**
 * A — Dossier: lezen, notitie, gespreksaanvraag, HR-gesprek, gespreksverslag,
 *     document, certificaat, skill, audit. Bij ELK dienstverband toegestaan,
 *     ook CONCEPT (intakegesprek) en UIT_DIENST (exitgesprek, nazorg,
 *     correctie). Dit is de fix voor de gemelde bug.
 * B — Voorbereidend: opleiding, uitrusting, abonnement, materieel, RI&E,
 *     NEN3140. ONBOARDING + IN_DIENST.
 * C — Operationeel/financieel: planning, werkbon, uren, weekstaat, declaratie,
 *     verlof, payroll. Toetst de PEILDATUM tegen het dienstverbandvenster, niet
 *     de platte waarde: vooruitplannen van iemand die terugkomt uit ziekte,
 *     uren namens een zieke, en payroll-correctie na uitdiensttreding werken
 *     daardoor allemaal.
 * D — Nudge: herinneringen, inactiviteitssignalen, km-stand,
 *     voertuigverklaring. Alleen IN_DIENST en alleen bij werkstatus ACTIEF.
 */
export type ActieKlasse = 'A' | 'B' | 'C' | 'D'

// ─── Predicaten ─────────────────────────────────────────────────────────────

/** Minimale vorm waarop de predicaten werken (Pick van Employee). */
export interface MedewerkerSelectieVelden {
  dienstverband: DienstverbandWaarde
  werkstatus: WerkstatusWaarde
  startDate: Date | null
  endDate: Date | null
  deletedAt: Date | null
}

/** Bestaat de medewerker nog (niet soft-deleted)? Klasse A-basis. */
export function bestaat(e: Pick<MedewerkerSelectieVelden, 'deletedAt'>): boolean {
  return e.deletedAt === null
}

/** Loopt de arbeidsrelatie nu, los van het venster? */
export function isInDienst(e: Pick<MedewerkerSelectieVelden, 'dienstverband' | 'deletedAt'>): boolean {
  return e.deletedAt === null && e.dienstverband === 'IN_DIENST'
}

/**
 * Valt de peildatum binnen het dienstverbandvenster?
 *
 * LET OP — `null`-tolerantie. Deze is overgenomen uit `planning.ts`
 * (`employmentWindowFilter`) en is de gevaarlijkste regressie van dit FO als je
 * 'm vergeet: een legacy-medewerker zonder `startDate` valt dan in een klap van
 * het planbord. NULL start/eind = onbekend -> tonen, niet wegfilteren.
 *
 * CONCEPT valt af (nog geen arbeidsrelatie). ONBOARDING/IN_DIENST/UIT_DIENST
 * worden alle drie op het venster getoetst: een uit-diensttreder mag nog uren
 * voor mei corrigeren, een onboarder mag vanaf zijn startdatum gepland worden.
 */
export function isInDienstOp(
  e: Pick<MedewerkerSelectieVelden, 'dienstverband' | 'startDate' | 'endDate' | 'deletedAt'>,
  op: Date,
): boolean {
  if (e.deletedAt !== null) return false
  if (e.dienstverband === 'CONCEPT') return false
  // UIT_DIENST ZONDER einddatum is een datafout, geen open einde. Zou je hier de
  // null-tolerantie toepassen (zoals bij ONBOARDING/IN_DIENST), dan is een
  // uit-diensttreder zonder einddatum op ELKE peildatum "in dienst" - voor
  // altijd. De null-tolerantie uit §12.7/§18.5 gaat over `startDate` (legacy),
  // niet hierover. Moet gelijk blijven aan `vensterDienstverbandFilter` in de
  // API-where-builders.
  if (e.dienstverband === 'UIT_DIENST' && e.endDate === null) return false
  // In NL-KALENDERDAGEN, niet in instants — zie `alsNlDag`. `startDate` en
  // `endDate` zijn INCLUSIEF: op je eerste en je laatste werkdag ben je in
  // dienst (en mag je dus uren boeken).
  const opDag = alsNlDag(op).getTime()
  if (e.startDate !== null && alsNlDag(e.startDate).getTime() > opDag) return false
  if (e.endDate !== null && alsNlDag(e.endDate).getTime() < opDag) return false
  return true
}

/**
 * batch-71 verplichte-certificaten (FO-541, spec 241 §B3) - GESTART op de peildatum: in dienst
 * volgens `isInDienstOp`, behalve de onboarder van wie de startdatum nog niet bekend is. De
 * null-tolerantie op `startDate` is er voor legacy-medewerkers (in dienst, datum onbekend); een
 * onboarder zonder datum is nog niet begonnen. Moet gelijk blijven aan `medewerkerSelectie.gestartOp`
 * in de API.
 */
export function isGestartOp(
  e: Pick<MedewerkerSelectieVelden, 'dienstverband' | 'startDate' | 'endDate' | 'deletedAt'>,
  op: Date,
): boolean {
  if (e.dienstverband === 'ONBOARDING' && e.startDate === null) return false
  return isInDienstOp(e, op)
}

/**
 * batch-48 offboarding-cockpit (spec 94 §3.1) — loopt de arbeidsrelatie op de
 * peildatum nog, óf is hij GEPLAND beëindigd op een datum die nog niet
 * verstreken is?
 *
 * Dit is het predicaat achter "uitdienst per een toekomstige datum is GEPLAND,
 * niet per direct". Zonder dit filter valt iemand die op 28-07 op uitdienst per
 * 30-08 wordt gezet diezelfde dag uit elke lijst en uit zijn verlofsaldi -
 * terwijl hij nog een maand gewoon werkt. Er komt bewust GEEN aparte "geplande
 * status" bij (spec A3): de einddatum + het venster zijn leidend, precies zoals
 * FO-340 het ontwierp.
 *
 * Verschil met {@link isInDienstOp}: die toetst het VOLLEDIGE venster (ook de
 * `startDate`). Hier telt alleen of het dienstverband nog loopt, zodat een
 * onboarder met een startdatum in de toekomst zichtbaar blijft in de
 * medewerkerlijst (die stond er vóór dit FO ook in). Moet gelijk blijven aan
 * `vensterDienstverbandFilter` in de API-where-builders
 * (`medewerkerSelectie.lopendDienstverband`).
 *
 * `UIT_DIENST` zonder einddatum is een datafout, geen open einde (zelfde
 * afweging als in {@link isInDienstOp}): die valt af.
 */
export function isDienstverbandLopendOp(
  e: Pick<MedewerkerSelectieVelden, 'dienstverband' | 'endDate' | 'deletedAt'>,
  op: Date,
): boolean {
  if (e.deletedAt !== null) return false
  if (e.dienstverband === 'CONCEPT') return false
  if (e.dienstverband !== 'UIT_DIENST') return true
  if (e.endDate === null) return false
  return alsNlDag(e.endDate).getTime() >= alsNlDag(op).getTime()
}

/**
 * batch-48 offboarding-cockpit (spec 94 §3.1) — is de uitdiensttreding GEPLAND
 * (einddatum in de toekomst) of al effectief?
 *
 * `null` = niet van toepassing (geen uitdiensttreding of geen einddatum).
 * `gepland: true` betekent: de medewerker is t/m de einddatum gewoon in dienst
 * en blijft overal zichtbaar; alleen de badge verandert.
 */
export function uitDienstStand(
  e: Pick<MedewerkerSelectieVelden, 'dienstverband' | 'endDate'>,
  op: Date = new Date(),
): { gepland: boolean; einddatum: Date } | null {
  if (e.dienstverband !== 'UIT_DIENST' || e.endDate === null) return null
  const einddatum = alsNlDag(e.endDate)
  return { gepland: einddatum.getTime() >= alsNlDag(op).getTime(), einddatum }
}

/**
 * De zichtbare badge-tekst bij een uitdiensttreding: `"Uit dienst per 30-08-2026"`
 * zolang de einddatum nog niet verstreken is, daarna gewoon `"Uit dienst"`
 * (spec 94 §3.1.2). `null` = geen uitdienst-badge nodig.
 */
export function uitDienstBadgeLabel(
  e: Pick<MedewerkerSelectieVelden, 'dienstverband' | 'endDate'>,
  op: Date = new Date(),
): string | null {
  const stand = uitDienstStand(e, op)
  if (!stand) return null
  if (!stand.gepland) return DIENSTVERBAND_LABELS.UIT_DIENST
  const dag = new Intl.DateTimeFormat('nl-NL', {
    timeZone: 'UTC',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(stand.einddatum)
  return `Uit dienst per ${dag}`
}

/** Inzetbaar nu: in dienst EN werkstatus ACTIEF. Voor suggesties/matching. */
export function isInzetbaar(
  e: Pick<MedewerkerSelectieVelden, 'dienstverband' | 'werkstatus' | 'deletedAt'>,
): boolean {
  return isInDienst(e) && e.werkstatus === 'ACTIEF'
}

/**
 * De gouden regel in code. Merk op dat `werkstatus` alleen bij klasse D
 * meespeelt — nergens anders blokkeert hij iets.
 *
 * @param op peildatum voor klasse C; default vandaag.
 */
export function magActie(e: MedewerkerSelectieVelden, klasse: ActieKlasse, op?: Date): boolean {
  if (!bestaat(e)) return false

  switch (klasse) {
    case 'A':
      // Dossier: altijd. Ook CONCEPT, ook UIT_DIENST, ook ziek.
      return true
    case 'B':
      return (DIENSTVERBAND_VOORBEREIDEND as readonly string[]).includes(e.dienstverband)
    case 'C':
      return isInDienstOp(e, op ?? new Date())
    case 'D':
      // De enige klasse waar werkstatus meespeelt (spec §3.4-uitzondering):
      // een zieke aanmanen om uren te boeken is niet fout maar wel ongepast.
      return isInDienst(e) && e.werkstatus === 'ACTIEF'
  }
}

/** Waarom is iemand afwezig? `null` = niet afwezig. Voor markering op het bord. */
export function afwezigReden(
  e: Pick<MedewerkerSelectieVelden, 'werkstatus'>,
): 'ziek' | 'langdurig-ziek' | 'non-actief' | null {
  switch (e.werkstatus) {
    case 'ZIEK':
      return 'ziek'
    case 'LANGDURIG_ZIEK':
      return 'langdurig-ziek'
    case 'NON_ACTIEF':
      return 'non-actief'
    case 'ACTIEF':
      return null
  }
}

// ─── NL-labels (zichtbare tekst; spec §18.14) ───────────────────────────────

export const DIENSTVERBAND_LABELS: Record<DienstverbandWaarde, string> = {
  CONCEPT: 'Concept',
  ONBOARDING: 'Onboarding',
  IN_DIENST: 'In dienst',
  UIT_DIENST: 'Uit dienst',
}

export const WERKSTATUS_LABELS: Record<WerkstatusWaarde, string> = {
  ACTIEF: 'Actief',
  ZIEK: 'Ziek',
  LANGDURIG_ZIEK: 'Langdurig ziek',
  NON_ACTIEF: 'Non-actief',
}

export function dienstverbandLabel(waarde: DienstverbandWaarde): string {
  return DIENSTVERBAND_LABELS[waarde] ?? waarde
}

export function werkstatusLabel(waarde: WerkstatusWaarde): string {
  return WERKSTATUS_LABELS[waarde] ?? waarde
}

/**
 * De waarschuwingstekst bij plannen op een afwezige (§10.3). `null` = geen
 * waarschuwing.
 *
 * Dit is NADRUKKELIJK een waarschuwing en nooit een blokkade: de planner weet
 * vaak meer dan het systeem (afspraak over werkhervatting, terugkeerdatum
 * bekend). Blokkeren zou hem dwingen het buiten de app om te regelen, en dan
 * klopt het bord helemaal niet meer.
 */
export function afwezigWaarschuwing(e: {
  firstName: string
  lastName: string
  werkstatus: WerkstatusWaarde
}): string | null {
  const reden = afwezigReden(e)
  if (!reden) return null
  const naam = `${e.firstName} ${e.lastName}`.trim()
  switch (reden) {
    case 'ziek':
      return `${naam} is ziek gemeld. Toch inplannen?`
    case 'langdurig-ziek':
      return `${naam} is langdurig ziek gemeld. Toch inplannen?`
    case 'non-actief':
      return `${naam} staat op non-actief. Toch inplannen?`
  }
}

// ─── Dual-write (fase 1-4; vervalt in fase 5) ───────────────────────────────

/**
 * FO-340 §15.5 — mapping terug naar de oude `status`-kolom, zodat elke gemiste
 * lezer blijft werken tot de kolom in fase 5 verdwijnt.
 *
 * De oude enum kan de twee lagen niet allebei uitdrukken, dus we kiezen de
 * lezing die het dichtst bij het oude gedrag ligt: het dienstverband is
 * leidend, behalve wanneer iemand in dienst is en afwezig — dan wint de
 * werkstatus, precies zoals `ZIEK` vroeger `ACTIEF` verdrong. Dat is de
 * ontwerpfout die we opheffen, maar zolang de oude lezers bestaan is 'm
 * spiegelen veiliger dan 'm negeren.
 */
export function dualWriteStatus(
  dienstverband: DienstverbandWaarde,
  werkstatus: WerkstatusWaarde,
): 'CONCEPT' | 'ONBOARDING' | 'ACTIEF' | 'INACTIEF' | 'ZIEK' | 'UITGEDIENST' {
  if (dienstverband === 'CONCEPT') return 'CONCEPT'
  if (dienstverband === 'ONBOARDING') return 'ONBOARDING'
  if (dienstverband === 'UIT_DIENST') return 'UITGEDIENST'
  // IN_DIENST — de werkstatus verdringt zoals vroeger.
  if (werkstatus === 'ZIEK' || werkstatus === 'LANGDURIG_ZIEK') return 'ZIEK'
  if (werkstatus === 'NON_ACTIEF') return 'INACTIEF'
  return 'ACTIEF'
}

/** De oude enum, voor de dual-write-richting `status` -> de twee lagen. */
export type LegacyEmployeeStatus =
  | 'CONCEPT'
  | 'ONBOARDING'
  | 'ACTIEF'
  | 'INACTIEF'
  | 'ZIEK'
  | 'VERLOF'
  | 'UITGEDIENST'

/**
 * FO-340 §15 — de ANDERE dual-write-richting: een schrijver die (nog) alleen de
 * oude `status` kent, moet toch consistente lagen achterlaten.
 *
 * Dit is exact even noodzakelijk als de mapping heen. Zonder deze richting landt
 * `employee.create({ status: 'ACTIEF' })` op `dienstverband: CONCEPT` (de
 * kolom-default) en is de medewerker meteen onzichtbaar voor élke `inDienst()`-
 * selectie — stiller en schadelijker dan de bug die dit FO oplost. Zelfde
 * mapping als de backfill-migratie (§15.1).
 *
 * Wordt toegepast door de `statusDualWriteExtension` in `@streep/db`, en
 * verdwijnt in fase 5 samen met de kolom.
 */
export function statusNaarLagen(status: LegacyEmployeeStatus): {
  dienstverband: DienstverbandWaarde
  werkstatus: WerkstatusWaarde
} {
  switch (status) {
    case 'CONCEPT':
      return { dienstverband: 'CONCEPT', werkstatus: 'ACTIEF' }
    case 'ONBOARDING':
      return { dienstverband: 'ONBOARDING', werkstatus: 'ACTIEF' }
    case 'ACTIEF':
      return { dienstverband: 'IN_DIENST', werkstatus: 'ACTIEF' }
    case 'ZIEK':
      // Werkstatus hoort uit de verzuimadministratie te komen; bij een create is
      // die er nog niet. De intentie van de schrijver bewaren is beter dan 'm
      // wissen — zelfde afweging als T42 in de backfill.
      return { dienstverband: 'IN_DIENST', werkstatus: 'ZIEK' }
    case 'VERLOF':
      // Verlof is geen werkstatus meer: het leeft in LeaveRequest (A3).
      return { dienstverband: 'IN_DIENST', werkstatus: 'ACTIEF' }
    case 'INACTIEF':
      return { dienstverband: 'IN_DIENST', werkstatus: 'NON_ACTIEF' }
    case 'UITGEDIENST':
      return { dienstverband: 'UIT_DIENST', werkstatus: 'NON_ACTIEF' }
  }
}
